// src/pages/system/Technicians.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon
} from '@mui/icons-material';

const TechnicianDialog = ({ open, onClose, technician, onSave, saving }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        status: 'active'
    });

    useEffect(() => {
        if (technician) {
            setFormData({
                name: technician.name || '',
                phone: technician.phone || '',
                email: technician.email || '',
                status: technician.status || 'active'
            });
        } else {
            setFormData({
                name: '',
                phone: '',
                email: '',
                status: 'active'
            });
        }
    }, [technician]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {technician ? 'Editar Técnico' : 'Nuevo Técnico'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            label="Nombre Completo"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            autoFocus
                        />
                    </Box>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            label="Teléfono"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                        />
                    </Box>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            label="Correo Electrónico"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </Box>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            label="Estado"
                            name="status"
                            select
                            value={formData.status}
                            onChange={handleChange}
                            SelectProps={{
                                native: true
                            }}
                        >
                            <option value="active">Activo</option>
                            <option value="inactive">Inactivo</option>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={saving}
                    >
                        {saving ? <CircularProgress size={24} /> : 'Guardar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

const Technicians = () => {
    const [technicians, setTechnicians] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchTechnicians();
    }, []);

    const fetchTechnicians = async () => {
        try {
            setLoading(true);
            const response = await fetch('/api/technicians/all', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener técnicos');
            }

            const data = await response.json();
            setTechnicians(data);
        } catch (error) {
            console.error('Error fetching technicians:', error);
            setError('Error al cargar datos. Intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveTechnician = async (formData) => {
        try {
            setSaving(true);

            const url = selectedTechnician
                ? `/api/technicians/${selectedTechnician.id}`
                : '/api/technicians';

            const method = selectedTechnician ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                throw new Error(selectedTechnician ? 'Error al actualizar técnico' : 'Error al crear técnico');
            }

            await fetchTechnicians();
            setDialogOpen(false);
            setSelectedTechnician(null);
        } catch (error) {
            console.error('Error saving technician:', error);
            setError('Error al guardar datos. Intenta nuevamente.');
        } finally {
            setSaving(false);
        }
    };

    const handleChangeTechnicianStatus = async (id, newStatus) => {
        try {
            const response = await fetch(`/api/technicians/${id}/status`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) {
                throw new Error('Error al cambiar estado del técnico');
            }

            await fetchTechnicians();
        } catch (error) {
            console.error('Error changing technician status:', error);
            setError('Error al cambiar estado. Intenta nuevamente.');
        }
    };

    const handleDeleteTechnician = async (id) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar este técnico?')) {
            return;
        }

        try {
            const response = await fetch(`/api/technicians/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al eliminar técnico');
            }

            await fetchTechnicians();
        } catch (error) {
            console.error('Error deleting technician:', error);
            setError('Error al eliminar técnico. Intenta nuevamente.');
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box className="p-6">
            <Box className="flex justify-between items-center mb-6">
                <Typography variant="h4">Técnicos</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setSelectedTechnician(null);
                        setDialogOpen(true);
                    }}
                >
                    Nuevo Técnico
                </Button>
            </Box>

            {error && (
                <Box mb={3}>
                    <Paper className="p-3 bg-red-50 text-red-700">
                        {error}
                    </Paper>
                </Box>
            )}

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nombre</TableCell>
                                <TableCell>Teléfono</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : technicians.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No hay técnicos registrados
                                    </TableCell>
                                </TableRow>
                            ) : (
                                technicians
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((technician) => (
                                        <TableRow key={technician.id}>
                                            <TableCell>{technician.name}</TableCell>
                                            <TableCell>{technician.phone}</TableCell>
                                            <TableCell>{technician.email}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={technician.status === 'active' ? 'Activo' : 'Inactivo'}
                                                    color={technician.status === 'active' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box className="flex space-x-1">
                                                    <Tooltip title="Editar">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => {
                                                                setSelectedTechnician(technician);
                                                                setDialogOpen(true);
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {technician.status === 'active' ? (
                                                        <Tooltip title="Desactivar">
                                                            <IconButton
                                                                size="small"
                                                                color="warning"
                                                                onClick={() => handleChangeTechnicianStatus(technician.id, 'inactive')}
                                                            >
                                                                <InactiveIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Activar">
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => handleChangeTechnicianStatus(technician.id, 'active')}
                                                            >
                                                                <ActiveIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    <Tooltip title="Eliminar">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteTechnician(technician.id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={technicians.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>

            <TechnicianDialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setSelectedTechnician(null);
                }}
                technician={selectedTechnician}
                onSave={handleSaveTechnician}
                saving={saving}
            />
        </Box>
    );
};

export default Technicians;